import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Lock, Zap, Star } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/landing/primitives";
import { STAKING_TIERS } from "@/data/content";

export default function StakingSection() {
  const [amount, setAmount] = useState(5000);
  const [apy, setApy] = useState(14);

  const yearly = Math.round((amount * apy) / 100);
  const monthly = Math.round(yearly / 12);
  const daily = (yearly / 365).toFixed(1);

  return (
    <section id="staking" className="relative py-20 md:py-28 scroll-mt-24" data-testid="section-staking">
      <div className="container-trt">
        <SectionHeading
          eyebrow="Staking"
          title="Put your TRT to work,"
          highlight="earn on-chain yield"
          sub="Stake TRT into secure, non-custodial contracts and earn competitive rewards. Choose the tier that matches your strategy."
        />

        <div className="mt-16 grid lg:grid-cols-3 gap-5">
          {STAKING_TIERS.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.1}>
              <div
                className={`relative rounded-2xl p-6 h-full card-hover ${tier.featured ? "glass-gold gold-glow" : "glass"}`}
                data-testid={`staking-tier-${tier.name.toLowerCase()}`}
              >
                {tier.featured && (
                  <span className="absolute -top-3 left-6 flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-gradient-to-r from-[var(--gold-1)] to-[var(--gold-3)] text-black px-3 py-1 rounded-full">
                    <Star className="w-3 h-3" /> Most Popular
                  </span>
                )}
                <div className="flex items-center gap-2 text-[var(--muted)]">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm font-mono">{tier.lock}</span>
                </div>
                <h3 className="mt-3 font-display text-2xl font-extrabold">{tier.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-5xl font-extrabold gold-text">{tier.apy}%</span>
                  <span className="text-[var(--muted)] mb-2 text-sm">APY</span>
                </div>
                <div className="mt-4 text-sm text-[var(--muted)]">Minimum stake</div>
                <div className="font-mono font-semibold">{tier.min}</div>
                <button
                  onClick={() => setApy(tier.apy)}
                  className={`mt-6 w-full ${tier.featured ? "btn-gold" : "btn-ghost"} justify-center`}
                >
                  <Zap className="w-4 h-4" /> Select tier
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Calculator */}
        <Reveal delay={0.15}>
          <div className="mt-8 glass rounded-3xl p-6 md:p-8" data-testid="staking-calculator">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-[var(--gold-2)]" />
              <h3 className="font-display text-xl font-bold">Reward Calculator</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[var(--muted)]">Stake amount</span>
                  <span className="font-mono text-[var(--gold-2)]">{amount.toLocaleString()} TRT</span>
                </div>
                <input
                  type="range" min="100" max="100000" step="100"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full accent-[var(--gold-2)]"
                  data-testid="calc-amount-slider"
                />
                <div className="flex justify-between text-sm mb-2 mt-6">
                  <span className="text-[var(--muted)]">APY</span>
                  <span className="font-mono text-[var(--gold-2)]">{apy}%</span>
                </div>
                <input
                  type="range" min="8" max="22" step="1"
                  value={apy}
                  onChange={(e) => setApy(Number(e.target.value))}
                  className="w-full accent-[var(--gold-2)]"
                  data-testid="calc-apy-slider"
                />
              </div>
              <div className="grid grid-cols-3 gap-3 notranslate" translate="no">
                {[
                  { l: "Daily", v: daily },
                  { l: "Monthly", v: monthly.toLocaleString() },
                  { l: "Yearly", v: yearly.toLocaleString() },
                ].map((r) => (
                  <div key={r.l} className="glass-gold rounded-2xl p-4 text-center">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--muted)]">{r.l}</div>
                    <div className="mt-2 font-display text-lg md:text-xl font-extrabold gold-text">{r.v}</div>
                    <div className="text-[10px] text-[var(--muted)] font-mono">TRT</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
